import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { matchModalPath } from "../detailRoutes";

function createMenuInstanceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `menu_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * URL → store sync for the slide-in menu. Mounted once in RootLayout.
 *
 * Watches the `?modal=…` query param. When it differs from current store
 * identity:
 *   - matched: writes the identity fields + a fresh menuInstanceId, clears
 *     navigationContext (this branch handles refresh, browser back, and
 *     shared-link opens — there's no caller intent to attribute, so analytics
 *     should treat as deep_link).
 *   - unmatched (modal removed): closes the slide-in.
 *
 * Click-driven opens write store identity + navContext synchronously before
 * navigating, so by the time this effect fires the URL identity already
 * matches the store and the no-op branch keeps navContext intact.
 */
export function useModalRouting() {
  const [searchParams] = useSearchParams();
  const modalParam = searchParams.get("modal");

  useEffect(() => {
    const matched = matchModalPath(modalParam);

    if (!matched) {
      const hadOpen = rootComponentsStore.openSlideInMenu.get();
      if (hadOpen) {
        rootComponentsStore.openSlideInMenu.set(undefined);
        rootComponentsStore.slideInMenuConfig.set({});
        rootComponentsStore.modalDepth.set(0);
      }
      return;
    }

    const currentMenu = rootComponentsStore.openSlideInMenu.get();
    const currentConfig = rootComponentsStore.slideInMenuConfig.get({
      noproxy: true,
    });

    const urlIdentity = {
      comicSlug: matched.params.comicSlug,
      releaseUuid: matched.params.releaseUuid,
      artistSlug: matched.params.artistSlug,
      username: matched.params.username,
      userSubpage: matched.params.subpage,
      bundleUuid: matched.params.bundleUuid,
    };

    const sameIdentity =
      currentMenu === matched.menu &&
      currentConfig.comicSlug === urlIdentity.comicSlug &&
      currentConfig.releaseUuid === urlIdentity.releaseUuid &&
      currentConfig.artistSlug === urlIdentity.artistSlug &&
      currentConfig.username === urlIdentity.username &&
      currentConfig.userSubpage === urlIdentity.userSubpage &&
      currentConfig.bundleUuid === urlIdentity.bundleUuid;

    if (!sameIdentity) {
      rootComponentsStore.openSlideInMenu.set(matched.menu);
      rootComponentsStore.slideInMenuConfig.set({
        ...urlIdentity,
        menuInstanceId: createMenuInstanceId(),
        navigationContext: undefined,
      });
    }
  }, [modalParam]);
}
